/**
 * Architecture Check Module
 * Detects layer violations in Clean Architecture
 */

import type { CheckContext, CodeReviewFinding } from '../types.js';
import { generateFindingId, LAYER_RULES } from '../types.js';

// Layer detection patterns for C#
const CSHARP_LAYER_PATTERNS: Record<string, RegExp[]> = {
  domain: [
    /namespace\s+\w+\.Domain/i,
    /SmartStack\.Domain/i,
  ],
  application: [
    /namespace\s+\w+\.Application/i,
    /SmartStack\.Application/i,
  ],
  infrastructure: [
    /namespace\s+\w+\.Infrastructure/i,
    /SmartStack\.Infrastructure/i,
    /\.Persistence\./i,
    /DbContext/i,
  ],
  api: [
    /namespace\s+\w+\.Api/i,
    /SmartStack\.Api/i,
    /\[ApiController\]/i,
  ],
};

// Import patterns to detect
const CSHARP_IMPORT_PATTERNS: Record<string, RegExp> = {
  domain: /using\s+[\w.]*\.Domain[.\w]*/gi,
  application: /using\s+[\w.]*\.Application[.\w]*/gi,
  infrastructure: /using\s+[\w.]*\.Infrastructure[.\w]*/gi,
  api: /using\s+[\w.]*\.Api[.\w]*/gi,
};

// TypeScript/React layer patterns
const TS_LAYER_VIOLATIONS = [
  {
    pattern: /from\s+['"].*Infrastructure.*['"]/gi,
    layer: 'infrastructure',
    message: 'Web/React code should not import from Infrastructure layer',
  },
  {
    pattern: /from\s+['"].*\.Api.*['"]/gi,
    layer: 'api',
    message: 'Web/React code should not import from Api layer directly',
  },
];

// Direct DbContext usage patterns
const DIRECT_DB_PATTERNS = [
  {
    pattern: /private\s+readonly\s+\w*DbContext/gi,
    name: 'Direct DbContext field',
  },
  {
    pattern: /new\s+\w*DbContext\s*\(/gi,
    name: 'DbContext instantiation',
  },
  {
    pattern: /IApplicationDbContext|ICoreDbContext/gi,
    name: 'DbContext interface',
    allowed: ['service', 'repository', 'handler'],
  },
];

export async function checkArchitecture(context: CheckContext): Promise<CodeReviewFinding[]> {
  const findings: CodeReviewFinding[] = [];

  for (const file of context.files) {
    const lines = file.content.split('\n');

    // C# architecture checks
    if (file.language === 'csharp') {
      const fileLayer = detectCsharpLayer(file.content, file.relativePath);

      if (fileLayer && LAYER_RULES[fileLayer]) {
        const rules = LAYER_RULES[fileLayer];

        // Check for forbidden imports
        for (const forbiddenLayer of rules.cannotImport) {
          const importPattern = CSHARP_IMPORT_PATTERNS[forbiddenLayer];
          if (!importPattern) continue;

          let match;
          importPattern.lastIndex = 0;
          while ((match = importPattern.exec(file.content)) !== null) {
            const lineNumber = getLineNumber(file.content, match.index);
            const lineContent = lines[lineNumber - 1]?.trim() || '';

            findings.push({
              id: generateFindingId('architecture'),
              category: 'architecture',
              severity: 'critical',
              title: `Layer Violation: ${capitalize(fileLayer)} → ${capitalize(forbiddenLayer)}`,
              description: `The ${capitalize(fileLayer)} layer should not import from the ${capitalize(forbiddenLayer)} layer. This violates Clean Architecture principles.`,
              file: file.relativePath,
              line: lineNumber,
              code: truncateCode(lineContent),
              suggestion: `Remove this import. ${getSuggestionForViolation(fileLayer, forbiddenLayer)}`,
              autoFixable: false,
            });
          }
        }
      }

      // Check for direct DbContext usage in wrong places
      // isService and isRepository checks reserved for future use
      const isController = /Controller\.cs$/i.test(file.relativePath);

      if (isController) {
        // Controllers should use services, not DbContext directly (unless SmartStack convention)
        for (const { pattern, name: _name } of DIRECT_DB_PATTERNS) {
          let match;
          pattern.lastIndex = 0;
          while ((match = pattern.exec(file.content)) !== null) {
            // SmartStack allows DbContext in controllers, but warn if complex queries
            const hasComplexQuery = /\.Include\(/.test(file.content) && /\.ThenInclude\(/.test(file.content);
            if (hasComplexQuery) {
              const lineNumber = getLineNumber(file.content, match.index);

              findings.push({
                id: generateFindingId('architecture'),
                category: 'architecture',
                severity: 'warning',
                title: 'Complex Query in Controller',
                description: 'Controllers with complex queries (multiple Includes) should consider moving logic to a service.',
                file: file.relativePath,
                line: lineNumber,
                suggestion: 'Consider extracting complex query logic to an Application layer service.',
                autoFixable: false,
              });
              break; // One warning per file
            }
          }
        }
      }

      // Check for service instantiation instead of DI
      const newServicePattern = /new\s+(\w+Service)\s*\(/gi;
      let match;
      while ((match = newServicePattern.exec(file.content)) !== null) {
        const lineNumber = getLineNumber(file.content, match.index);
        const lineContent = lines[lineNumber - 1]?.trim() || '';

        // Skip if in test files
        if (file.relativePath.includes('Test') || file.relativePath.includes('.test.')) continue;

        findings.push({
          id: generateFindingId('architecture'),
          category: 'architecture',
          severity: 'critical',
          title: 'Service Instantiation Instead of DI',
          description: `Direct instantiation of ${match[1]} detected. Services should be injected via constructor, not instantiated with 'new'.`,
          file: file.relativePath,
          line: lineNumber,
          code: truncateCode(lineContent),
          suggestion: 'Inject the service interface via constructor dependency injection.',
          autoFixable: false,
        });
      }
    }

    // TypeScript/React architecture checks
    if (['typescript', 'tsx'].includes(file.language) && file.layer === 'web') {
      for (const { pattern, message } of TS_LAYER_VIOLATIONS) {
        let match;
        pattern.lastIndex = 0;
        while ((match = pattern.exec(file.content)) !== null) {
          const lineNumber = getLineNumber(file.content, match.index);
          const lineContent = lines[lineNumber - 1]?.trim() || '';

          findings.push({
            id: generateFindingId('architecture'),
            category: 'architecture',
            severity: 'critical',
            title: 'Web Layer Violation',
            description: message,
            file: file.relativePath,
            line: lineNumber,
            code: truncateCode(lineContent),
            suggestion: 'Use API client services to communicate with the backend instead of direct imports.',
            autoFixable: false,
          });
        }
      }
    }
  }

  return findings;
}

// Helper functions
function detectCsharpLayer(content: string, filePath: string): string | null {
  // First check file path
  const pathLower = filePath.toLowerCase();
  if (pathLower.includes('/domain/') || pathLower.includes('\\domain\\')) return 'domain';
  if (pathLower.includes('/application/') || pathLower.includes('\\application\\')) return 'application';
  if (pathLower.includes('/infrastructure/') || pathLower.includes('\\infrastructure\\')) return 'infrastructure';
  if (pathLower.includes('/api/') || pathLower.includes('\\api\\')) return 'api';

  // Then check namespace patterns
  for (const [layer, patterns] of Object.entries(CSHARP_LAYER_PATTERNS)) {
    for (const pattern of patterns) {
      if (pattern.test(content)) {
        return layer;
      }
    }
  }

  return null;
}

function getSuggestionForViolation(fromLayer: string, toLayer: string): string {
  if (fromLayer === 'domain' && toLayer === 'application') {
    return 'Domain entities should not depend on Application layer. Move shared interfaces to Domain.';
  }
  if (fromLayer === 'application' && toLayer === 'infrastructure') {
    return 'Define an interface in Application layer and implement it in Infrastructure.';
  }
  if (fromLayer === 'api' && toLayer === 'web') {
    return 'API controllers should not depend on Web layer.';
  }
  if (toLayer === 'infrastructure') {
    return 'Use dependency injection with interfaces defined in Application layer.';
  }
  return 'Refactor to respect Clean Architecture layer boundaries.';
}

function getLineNumber(content: string, index: number): number {
  return content.slice(0, index).split('\n').length;
}

function truncateCode(code: string, maxLength: number = 100): string {
  if (code.length <= maxLength) return code;
  return code.slice(0, maxLength - 3) + '...';
}

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
